import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Form,
  Row,
  Col,
  Input,
  Select,
  message,
  Spin,
  Modal,
  Radio,
  Table,
  Tag
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import type { ColumnsType } from 'antd/es/table';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminWordApi } from '@/services/api';

const { Title } = Typography;

interface WordDetail {
  id: string;
  word: string;
  meaning: string;
  ttsGender: string;
  ttsAge: string;
  ttsSpeed: string;
  ttsPitch: string;
  registrant: string;
  registrationDate: string;
}

interface UploadWordData {
  key: string;
  no: number;
  word: string;
  meaning: string;
  uploadStatus: 'pending' | 'success' | 'error';
  errorMessage?: string;
}

const assignDescendingNo = (list: UploadWordData[]) =>
  list.map((item, idx, arr) => ({
    ...item,
    no: arr.length - idx,
  }));

const AdminWordDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wordId } = useParams<{ wordId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordData, setWordData] = useState<WordDetail | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [learningHistoryModalVisible, setLearningHistoryModalVisible] = useState(false);
  const [registrationType, setRegistrationType] = useState<'individual' | 'excel'>('individual');
  const [uploadData, setUploadData] = useState<UploadWordData[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fromPath = (location.state as { from?: string } | null)?.from;
  const audioRef = useRef<HTMLAudioElement>(null);
  const autoPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [originalWord, setOriginalWord] = useState<string | null>(null);
  const [isWordChanged, setIsWordChanged] = useState<boolean>(false);
  const [originalTtsGender, setOriginalTtsGender] = useState<string | null>(null);
  const [originalTtsAge, setOriginalTtsAge] = useState<string | null>(null);
  const [originalTtsSpeed, setOriginalTtsSpeed] = useState<string | null>(null);
  const [originalTtsPitch, setOriginalTtsPitch] = useState<string | null>(null);
  const [isTtsOptionChanged, setIsTtsOptionChanged] = useState<boolean>(false);

  const isEditMode = !!wordId;

  // TTS 옵션들 (더미 데이터 - 실제로는 API에서 가져와야 함)
  const genderOptions = [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' }
  ];

  const ageOptions = [
    { value: 'child', label: '아동' },
    { value: 'young', label: '청소년' },
    { value: 'adult', label: '성인' },
    { value: 'elder', label: '노년' }
  ];

  const speedOptions = [
    { value: 'slow', label: '느림' },
    { value: 'normal', label: '보통' },
    { value: 'fast', label: '빠름' }
  ];

  const pitchOptions = [
    { value: 'low', label: '낮음' },
    { value: 'medium', label: '보통' },
    { value: 'high', label: '높음' }
  ];

  const handleBackToList = () => {
    navigate(fromPath || '/admin/word');
  };

  const handlePreview = async (opts?: { silent?: boolean }) => {
    const word = form.getFieldValue('word');
    const gender = form.getFieldValue('ttsGender');
    const age = form.getFieldValue('ttsAge');
    const speed = form.getFieldValue('ttsSpeed');
    const pitch = form.getFieldValue('ttsPitch');
    if (!word || word.trim() === '') {
      if (!opts?.silent) {
        message.warning('단어를 입력해주세요.');
      }
      return;
    }
    try {
      const blob: Blob = await adminWordApi.preview({ word, gender, age, speed, pitch });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {
          message.warning('브라우저에서 자동 재생이 차단되었습니다. 플레이 버튼을 눌러주세요.');
        });
      }, 0);
    } catch (e: any) {
      if (e?.response?.data?.message) {
        message.error(e.response.data.message);
      } else {
        message.error('미리듣기에 실패했습니다.');
      }
    }
  };

  const scheduleAutoPreview = () => {
    if (autoPreviewTimerRef.current) {
      clearTimeout(autoPreviewTimerRef.current);
    }
    autoPreviewTimerRef.current = setTimeout(() => {
      handlePreview({ silent: true });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (autoPreviewTimerRef.current) clearTimeout(autoPreviewTimerRef.current);
    };
  }, [previewUrl]);

  const confirmWordUpdate = () =>
    new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <span>단어 수정</span>
          </div>
        ),
        content: '수정하시겠습니까?',
        icon: null,
        okText: '확인',
        cancelText: '취소',
        centered: true,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleSubmit = async (values: any) => {
    if (isEditMode) {
      const confirmed = await confirmWordUpdate();
      if (!confirmed) return;
    }
    setSaving(true);
    try {
      if (isEditMode) {
        // 수정 모드
        const payload = {
          word: values.word,
          meaning: values.meaning,
          gender: values.ttsGender,
          age: values.ttsAge,
          speed: values.ttsSpeed,
          pitch: values.ttsPitch,
        };
        await adminWordApi.update(Number(wordId), payload);
        message.success('단어가 성공적으로 수정되었습니다.');
      } else {
        // 등록 모드
        if (registrationType === 'excel') {
          if (!uploadData.length) {
            message.warning('엑셀 업로드 데이터를 추가해주세요.');
            return;
          }
          const gender = values.ttsGender || 'female';
          const age = values.ttsAge || 'adult';
          const speed = values.ttsSpeed || 'normal';
          const pitch = values.ttsPitch || 'medium';
          const registrant = values.registrant || '';

          let successCount = 0;
          let failCount = 0;
          const newList: UploadWordData[] = [...uploadData];
          for (let i = 0; i < newList.length; i++) {
            const item = newList[i];
            if (item.uploadStatus === 'success') continue; // 이미 성공한 항목은 스킵
            if (item.uploadStatus === 'error' && (item.errorMessage?.includes('동일 TTS 유형'))) {
              // 사전 중복 검사로 등록불가 표시된 항목 스킵
              continue;
            }
            try {
              const res: any = await adminWordApi.create({
                word: item.word,
                meaning: item.meaning,
                gender,
                age,
                speed,
                pitch,
                registrant,
              });
              if (res?.success) {
                newList[i] = { ...item, uploadStatus: 'success' } as UploadWordData;
                successCount += 1;
              } else {
                newList[i] = { ...item, uploadStatus: 'error', errorMessage: res?.message || '실패' } as UploadWordData;
                failCount += 1;
              }
            } catch (e: any) {
              const msg = e?.response?.data?.message || '실패';
              newList[i] = { ...item, uploadStatus: 'error', errorMessage: msg } as UploadWordData;
              failCount += 1;
            }
          }
          setUploadData(assignDescendingNo([...newList]));
          if (successCount > 0) {
            message.success(`${successCount}건 등록 성공` + (failCount ? `, ${failCount}건 실패` : ''));
          } else if (failCount > 0) {
            message.error(`${failCount}건 등록 실패`);
          } else {
            message.info('등록할 항목이 없습니다.');
          }
        } else {
          const payload = {
            word: values.word,
            meaning: values.meaning,
            gender: values.ttsGender || 'female',
            age: values.ttsAge || 'adult',
            speed: values.ttsSpeed || 'normal',
            pitch: values.ttsPitch || 'medium',
            registrant: values.registrant || ''
          };
          const res: any = await adminWordApi.create(payload);
          if (res?.success) {
            setSuccessModalVisible(true);
          }
        }
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      if (typeof msg === 'string' && msg.includes('중복')) {
        setDuplicateModalVisible(true);
      } else {
        message.error(msg || (isEditMode ? '단어 수정에 실패했습니다.' : '단어 등록에 실패했습니다.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    navigate('/admin/word');
  };

  const handleDuplicateModalOk = () => {
    setDuplicateModalVisible(false);
  };

  const handleLearningHistoryConfirm = async () => {
    setLearningHistoryModalVisible(false);
    setSaving(true);
    try {
      // 실제 수정 처리
      console.log('학습이력이 있는 단어 수정 처리');
      message.success('단어가 성공적으로 수정되었습니다.');
      setWordData({ ...wordData!, ...form.getFieldsValue() });
    } catch (error) {
      message.error('단어 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleLearningHistoryCancel = () => {
    setLearningHistoryModalVisible(false);
    setSaving(false);
  };

  const handleCancel = () => {
    navigate('/admin/word');
  };

  // 업로드 데이터 테이블 컬럼 정의
  const uploadColumns: ColumnsType<UploadWordData> = [
    {
      title: '순번',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center'
    },
    {
      title: '단어',
      dataIndex: 'word',
      key: 'word',
      width: 200,
      align: 'center'
    },
    {
      title: '해석',
      dataIndex: 'meaning',
      key: 'meaning',
      width: 300,
      align: 'center'
    },
    {
      title: '업로드 상태',
      dataIndex: 'uploadStatus',
      key: 'uploadStatus',
      width: 160,
      align: 'center',
      render: (status: string, record: UploadWordData) => {
        if (status === 'error') {
          const text = record?.errorMessage?.includes('동일 TTS 유형') ? '등록불가' : (record.errorMessage || '실패');
          return <Tag color="#ff4d4f">{text}</Tag>;
        }
        if (status === 'success') return <Tag color="#52c41a">성공</Tag>;
        return <Tag color="#1890ff">대기중</Tag>;
      }
    },
    {
      title: '미리듣기',
      key: 'preview',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button size="small" onClick={() => handleRowPreview(record.word)} icon={<PlayCircleOutlined />}>재생</Button>
      )
    },
    {
      title: '삭제',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteUploadItem(record.key)}
          size="small"
        >
          삭제
        </Button>
      )
    }
  ];

  // 업로드 항목 삭제 함수
  const handleDeleteUploadItem = (key: string) => {
    setUploadData(prev => assignDescendingNo(prev.filter(item => item.key !== key)));
  };

  // 엑셀 업로드 처리 함수
  const handleExcelTemplateDownload = () => {
    const wb = XLSX.utils.book_new();
    const header = ['단어','해석'];
    const ws = XLSX.utils.aoa_to_sheet([header]);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'word_template.xlsx');
  };

  const handleExcelUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async () => {
      const file = (input.files && input.files[0]) || null;
      if (!file) return;
      try {
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows || rows.length <= 1) {
          message.warning('데이터가 없습니다. 2행부터 단어, 해석을 입력해주세요.');
          return;
        }
        const list: UploadWordData[] = [];
        for (let i = 1; i < rows.length; i++) { // 1행 헤더 무시 후 2행부터
          const r = rows[i] || [];
          const word = String(r[0] || '').trim();
          const meaning = String(r[1] || '').trim();
          if (!word && !meaning) continue; // 빈 행 스킵
          list.push({
            key: String(i),
            no: 0,
            word,
            meaning,
            uploadStatus: 'pending'
          });
        }
        // 현재 화면의 TTS 설정과 동일 옵션으로 DB 존재 여부 병렬 조회
        const gender = form.getFieldValue('ttsGender') || 'female';
        const age = form.getFieldValue('ttsAge') || 'adult';
        const speed = form.getFieldValue('ttsSpeed') || 'normal';
        const pitch = form.getFieldValue('ttsPitch') || 'medium';

        const checks = await Promise.all(list.map(item => 
          adminWordApi.exists({ word: item.word, gender, age, speed, pitch }).catch(() => null)
        ));
        const withStatus = list.map((item, idx) => {
          const exists = checks[idx]?.data?.exists ?? checks[idx]?.exists ?? false;
          if (exists) {
            return { ...item, uploadStatus: 'error', errorMessage: '등록불가(동일 TTS 유형 존재)' } as UploadWordData;
          }
          return item;
        });
        setUploadData(assignDescendingNo(withStatus));
        message.success('엑셀 데이터가 로드되었습니다.');
      } catch (e) {
        message.error('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    input.click();
  };

  const handleRowPreview = async (wordText: string) => {
    try {
      const gender = form.getFieldValue('ttsGender') || 'female';
      const age = form.getFieldValue('ttsAge') || 'adult';
      const speed = form.getFieldValue('ttsSpeed') || 'normal';
      const pitch = form.getFieldValue('ttsPitch') || 'medium';
      const blob: Blob = await adminWordApi.preview({ word: wordText, gender, age, speed, pitch });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {
          message.warning('브라우저에서 자동 재생이 차단되었습니다. 플레이 버튼을 눌러주세요.');
        });
      }, 0);
    } catch {
      message.error('미리듣기에 실패했습니다.');
    }
  };

  const recheckUploadDuplicates = async () => {
    if (registrationType !== 'excel' || uploadData.length === 0) return;
    const gender = form.getFieldValue('ttsGender') || 'female';
    const age = form.getFieldValue('ttsAge') || 'adult';
    const speed = form.getFieldValue('ttsSpeed') || 'normal';
    const pitch = form.getFieldValue('ttsPitch') || 'medium';
    const list = uploadData.slice();
    try {
      const checks = await Promise.all(list.map(item =>
        adminWordApi.exists({ word: item.word, gender, age, speed, pitch }).catch(() => null)
      ));
      const withStatus: UploadWordData[] = list.map((item, idx) => {
        const exists = checks[idx]?.data?.exists ?? checks[idx]?.exists ?? false;
        if (exists) {
          return { ...item, uploadStatus: 'error' as const, errorMessage: '등록불가(동일 TTS 유형 존재)' };
        }
        // 기존 성공은 유지, 그 외는 대기중으로 표시
        return item.uploadStatus === 'success' ? item : { ...item, uploadStatus: 'pending' as const, errorMessage: undefined };
      });
      setUploadData(assignDescendingNo(withStatus));
    } catch {
      // 무시 (상태 유지)
    }
  };

  useEffect(() => {
    const load = async () => {
      if (isEditMode) {
        setLoading(true);
        try {
          const res: any = await adminWordApi.detail(Number(wordId));
          if (res?.success) {
            const d = res.data || {};
            const mapped: WordDetail = {
              id: d.code || '-',
              word: d.word,
              meaning: d.meaning,
              ttsGender: d.gender,
              ttsAge: d.age,
              ttsSpeed: d.speed,
              ttsPitch: d.pitch,
              registrant: d.registrant || '-',
              registrationDate: (d.createdAt || '').split('T')[0] || ''
            };
            setWordData(mapped);
            setAudioUrl(d.audioUrl || null);
            setOriginalWord(d.word || '');
            setOriginalTtsGender(d.gender || '');
            setOriginalTtsAge(d.age || '');
            setOriginalTtsSpeed(d.speed || '');
            setOriginalTtsPitch(d.pitch || '');
            setIsWordChanged(false);
            setIsTtsOptionChanged(false);
            form.setFieldsValue({
              word: mapped.word,
              meaning: mapped.meaning,
              ttsGender: mapped.ttsGender,
              ttsAge: mapped.ttsAge,
              ttsSpeed: mapped.ttsSpeed,
              ttsPitch: mapped.ttsPitch,
              registrant: mapped.registrant
            });
          }
        } finally {
          setLoading(false);
        }
      } else {
        form.setFieldsValue({
          registrant: '관리자',
          ttsGender: 'female',
          ttsAge: 'adult',
          ttsSpeed: 'normal',
          ttsPitch: 'medium'
        });
      }
    };
    load();
  }, [wordId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '단어 상세' : '단어 등록'}
          </Title>
          {isEditMode && wordData && (
            <div style={{ color: '#666' }}>단어코드: {wordData.id}</div>
          )}
        </div>

        {/* 단어 등록 유형 선택 */}
        {!isEditMode && (
          <Card style={{ marginBottom: 24 }}>
            <Form.Item
              label="단어 등록 유형"
              layout="vertical"
              style={{ marginBottom: 0 }}
            >
              <Radio.Group
                value={registrationType}
                onChange={(e) => setRegistrationType(e.target.value)}
                size="large"
              >
                <Space>
                  <Radio.Button value="individual">개별 등록</Radio.Button>
                  <Radio.Button value="excel">엑셀 일괄 업로드</Radio.Button>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Card>
        )}

        {/* 엑셀 일괄 업로드 영역 - 엑셀 선택 시에만 표시 */}
        {!isEditMode && registrationType === 'excel' && (
          <>
            <Card style={{ marginBottom: 24 }}>
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>
                  엑셀 일괄 업로드
                </div>
                <div style={{ color: '#666', marginBottom: 32 }}>
                  엑셀 파일을 업로드하여 여러 단어를 한 번에 등록할 수 있습니다.
                </div>
                <Space size="large">
                  <Button 
                    icon={<DownloadOutlined />} 
                    size="large"
                    onClick={handleExcelTemplateDownload}
                  >
                    엑셀 양식 다운로드
                  </Button>
                  <Button 
                    icon={<UploadOutlined />} 
                    type="primary"
                    size="large"
                    onClick={handleExcelUpload}
                  >
                    엑셀 파일 업로드
                  </Button>
                </Space>
              </div>
            </Card>

            {/* 업로드 데이터 테이블 */}
            {uploadData.length > 0 && (
              <Card style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ margin: 0 }}>업로드 데이터</h3>
                </div>
                <Table
                  columns={uploadColumns}
                  dataSource={uploadData}
                  pagination={{ pageSize: 10 }}
                  size="middle"
                  scroll={{ x: 800 }}
                />
              </Card>
            )}
          </>
        )}

        {/* 등록 폼 (공통) */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={async (changed, all) => {
              if (Object.prototype.hasOwnProperty.call(changed, 'word')) {
                const cur = (changed as any).word ?? '';
                const base = originalWord ?? '';
                const changedFlag = cur !== base;
                setIsWordChanged(changedFlag);
                if (!changedFlag && !isTtsOptionChanged) setPreviewUrl(null);
              }
              const ttsFieldChanged =
                Object.prototype.hasOwnProperty.call(changed, 'ttsGender') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsAge') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsSpeed') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsPitch');
              const curGender = (all as any).ttsGender ?? '';
              const curAge = (all as any).ttsAge ?? '';
              const curSpeed = (all as any).ttsSpeed ?? '';
              const curPitch = (all as any).ttsPitch ?? '';
              const baseGender = originalTtsGender ?? '';
              const baseAge = originalTtsAge ?? '';
              const baseSpeed = originalTtsSpeed ?? '';
              const basePitch = originalTtsPitch ?? '';
              const ttsChanged = curGender !== baseGender || curAge !== baseAge || curSpeed !== baseSpeed || curPitch !== basePitch;
              setIsTtsOptionChanged(ttsChanged);
              if (!ttsChanged && !isWordChanged) setPreviewUrl(null);
              if (registrationType === 'excel' && (Object.prototype.hasOwnProperty.call(changed, 'ttsGender') || Object.prototype.hasOwnProperty.call(changed, 'ttsAge') || Object.prototype.hasOwnProperty.call(changed, 'ttsSpeed') || Object.prototype.hasOwnProperty.call(changed, 'ttsPitch'))) {
                await recheckUploadDuplicates();
              }
              if (registrationType === 'individual' && ttsFieldChanged) {
                scheduleAutoPreview();
              }
            }}
          >
            {registrationType === 'individual' && (
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    label="단어"
                    name="word"
                    rules={[
                      { required: true, message: '단어를 입력해주세요' },
                      { min: 1, max: 50, message: '단어는 1~50자로 입력해주세요' }
                    ]}
                  >
                    <Input
                      placeholder="단어를 입력하세요"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="해석"
                    name="meaning"
                    rules={[
                      { required: true, message: '해석을 입력해주세요' },
                      { min: 1, max: 100, message: '해석은 1~100자로 입력해주세요' }
                    ]}
                  >
                    <Input
                      placeholder="해석을 입력하세요"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Card style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}>TTS 설정</p>
              
              <Row gutter={20}>
                <Col span={4}>
                  <Form.Item
                    label="음성성별"
                    name="ttsGender"
                    rules={[
                      { required: true, message: '음성성별을 선택해주세요' }
                    ]}
                  >
                    <Select
                      placeholder="음성성별을 선택하세요"
                      size="large"
                      options={genderOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label="연령대"
                    name="ttsAge"
                    rules={[
                      { required: true, message: '연령대를 선택해주세요' }
                    ]}
                  >
                    <Select
                      placeholder="연령대를 선택하세요"
                      size="large"
                      options={ageOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label="속도"
                    name="ttsSpeed"
                    rules={[
                      { required: true, message: '속도를 선택해주세요' }
                    ]}
                  >
                    <Select
                      placeholder="속도를 선택하세요"
                      size="large"
                      options={speedOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label="피치"
                    name="ttsPitch"
                    rules={[
                      { required: true, message: '피치를 선택해주세요' }
                    ]}
                  >
                    <Select
                      placeholder="피치를 선택하세요"
                      size="large"
                      options={pitchOptions}
                    />
                  </Form.Item>
                </Col>
                {registrationType === 'individual' && (
                  <Col span={8}>
                    <Form.Item label="미리듣기">
                      <Button 
                        icon={<PlayCircleOutlined />} 
                        type="primary" 
                        ghost
                        onClick={() => handlePreview()}
                        size="large"
                        style={{ width: '100%', height: 40, fontSize: 16 }}
                        disabled={isEditMode && !(isWordChanged || isTtsOptionChanged)}
                      >
                        미리듣기
                      </Button>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Card>

            {(previewUrl || audioUrl) && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 14, marginBottom: 8 }}>미리듣기</p>
                <audio ref={audioRef} controls src={previewUrl || audioUrl || undefined} style={{ width: '100%' }} preload="auto" />
              </div>
            )}

            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  label="등록자"
                  name="registrant"
                >
                  <Input
                    disabled
                    size="large"
                  />
                </Form.Item>
              </Col>
              {isEditMode && wordData && (
                <Col span={8}>
                  <Form.Item
                    label="등록일"
                  >
                    <Input
                      value={wordData.registrationDate}
                      disabled
                      size="large"
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {/* 버튼 영역 */}
            <div style={{ textAlign: 'right', marginTop: 32 }}>
              <Space size="middle">
                <Button 
                  onClick={handleCancel}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  취소
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saving}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </Button>
              </Space>
            </div>
          </Form>

        {/* 성공 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
              <span>등록 완료</span>
            </div>
          }
          open={successModalVisible}
          onOk={handleSuccessModalOk}
          onCancel={handleSuccessModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleSuccessModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              단어 등록 및 음원 생성이 완료되었습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              등록된 단어는 단어 리스트에서 확인할 수 있습니다.
            </div>
          </div>
        </Modal>

        {/* 중복 단어 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>중복 단어</span>
            </div>
          }
          open={duplicateModalVisible}
          onOk={handleDuplicateModalOk}
          onCancel={handleDuplicateModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleDuplicateModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              이미 등록된 단어입니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              동일한 단어가 이미 등록되어 있습니다. 다른 단어를 입력해주세요.
            </div>
          </div>
        </Modal>

        {/* 학습이력 확인 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>학습이력 확인</span>
            </div>
          }
          open={learningHistoryModalVisible}
          onOk={handleLearningHistoryConfirm}
          onCancel={handleLearningHistoryCancel}
          footer={[
            <Button key="cancel" onClick={handleLearningHistoryCancel}>
              취소
            </Button>,
            <Button key="confirm" type="primary" onClick={handleLearningHistoryConfirm}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              학습이력이 있는 단어장입니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              수정하시겠습니까?
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminWordDetail; 